import { lazy, useMemo } from 'react';
import { Navigate, RouteObject } from 'react-router';
import { FormattedMessage } from 'react-intl';

// components
import Loadable from 'components/Loadable';

// api's
import { getCompany, getPropertyDetail, PropertyDetail } from 'utils/raw-api';

// types
import { Company } from 'types/company';

// hooks
import useDynamicConfig from 'hooks/useDynamicConfig';
import useRoleChecks from 'hooks/useRoleChecks';
import useUserRole from 'hooks/useUserRole';

// pages
const PropertyLevel = Loadable(lazy(() => import('pages/properties/property-level')));
const PropertyOverview = Loadable(lazy(() => import('pages/properties/property-overview')));
const PropertyBuildings = Loadable(lazy(() => import('pages/properties/property-buildings')));
const PropertyAlerts = Loadable(lazy(() => import('pages/properties/property-alerts')));
const PropertyMicroMeters = Loadable(lazy(() => import('pages/properties/property-micro-meters')));
const PropertySubmissions = Loadable(lazy(() => import('pages/properties/property-submissions')));
const PropertyUsers = Loadable(lazy(() => import('pages/properties/property-users')));
const PropertyCharts = Loadable(lazy(() => import('pages/properties/property-charts')));
const PropertyInvoices = Loadable(lazy(() => import('pages/properties/property-invoices')));
const PropertySavings = Loadable(lazy(() => import('pages/properties/property-savings')));
const PropertyCityBills = Loadable(lazy(() => import('pages/properties/property-city-bills')));

const usePropertyLevelRoutes = () => {
  const { SYSTEM_ROLES, COMPANY_ROLES, PROPERTY_ROLES } = useDynamicConfig();
  const { isAboveCompanyManager, isAboveSystemSupport } = useRoleChecks();
  const { activeRole } = useUserRole();

  const userCompany = activeRole?.company;

  const routes: RouteObject = useMemo(() => {
    return {
      path: 'companies',
      handle: {
        crumb: () => (isAboveSystemSupport ? null : <FormattedMessage id="companies" />),
        allowedRoles: [...SYSTEM_ROLES, ...COMPANY_ROLES, ...PROPERTY_ROLES]
      },
      children: [
        {
          path: ':companyId',
          loader: async ({ params }) => {
            if (params.companyId && !isAboveCompanyManager) {
              return await getCompany(Number(params.companyId));
            }
          },
          handle: {
            crumb: (data?: Company) => data && <>{data.name}</>
          },
          children: [
            {
              path: 'properties',
              handle: {
                crumb: () => (isAboveCompanyManager ? null : <FormattedMessage id="properties" />)
              },
              children: [
                {
                  path: ':propertyId',
                  element: <PropertyLevel />,
                  loader: async ({ params }) => {
                    if (params.propertyId) {
                      return await getPropertyDetail(Number(params.propertyId));
                    }
                  },
                  handle: {
                    crumb: ({ property }: PropertyDetail) => <>{property?.name}</>,
                    title: ({ property }: PropertyDetail) => <>{property?.name}</>,
                    subtitle: ({ city, state, country }: PropertyDetail) => {
                      const address = [city?.name, state?.name, country?.name].filter((x) => x).join(', ');
                      return <>{address}</>;
                    }
                  },
                  children: [
                    {
                      index: true,
                      element: <Navigate to="buildings" replace />
                    },
                    {
                      path: 'overview',
                      element: <PropertyOverview />,
                      handle: {
                        crumb: () => <FormattedMessage id="overview" />
                      }
                    },
                    {
                      path: 'buildings',
                      element: <PropertyBuildings />,
                      handle: {
                        crumb: () => <FormattedMessage id="buildings" />
                      }
                    },
                    {
                      path: 'alerts',
                      element: <PropertyAlerts />,
                      handle: {
                        crumb: () => <FormattedMessage id="alerts" />
                      }
                    },
                    {
                      path: 'micrometers',
                      element: <PropertyMicroMeters />,
                      handle: {
                        crumb: () => <FormattedMessage id="micrometers" />
                      }
                    },
                    {
                      path: 'submissions',
                      element: <PropertySubmissions />,
                      handle: {
                        crumb: () => <FormattedMessage id="submissions" />
                      }
                    },
                    {
                      path: 'users',
                      element: <PropertyUsers />,
                      handle: {
                        crumb: () => <FormattedMessage id="users" />
                      }
                    },
                    {
                      path: 'analytics',
                      element: <PropertyCharts />,
                      handle: {
                        crumb: () => <FormattedMessage id="analytics" />
                      }
                    },
                    {
                      path: 'savings',
                      element: <PropertySavings />,
                      handle: {
                        crumb: () => <FormattedMessage id="savings" />,
                        allowedRoles:
                          (userCompany?.expose_savings || 0) === 0 ? SYSTEM_ROLES : [...SYSTEM_ROLES, ...COMPANY_ROLES, ...PROPERTY_ROLES]
                      }
                    },
                    {
                      path: 'billing',
                      handle: {
                        crumb: () => <FormattedMessage id="billing" />,
                        allowedRoles: [...SYSTEM_ROLES, ...COMPANY_ROLES, ...PROPERTY_ROLES]
                      },
                      children: [
                        {
                          index: true,
                          element: <Navigate to="city-bills" replace />
                        },
                        {
                          path: 'city-bills',
                          element: <PropertyCityBills />,
                          handle: {
                            crumb: () => <FormattedMessage id="city-bills" />
                          }
                        },
                        {
                          path: 'invoices',
                          element: <PropertyInvoices />,
                          handle: {
                            crumb: () => <FormattedMessage id="invoices" />
                          }
                        }
                      ]
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    };
  }, [SYSTEM_ROLES, COMPANY_ROLES, PROPERTY_ROLES, isAboveCompanyManager, isAboveSystemSupport, userCompany]);
  return routes;
};

export default usePropertyLevelRoutes;
